import { useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { KeyRound, Loader2 } from 'lucide-react';

const MIN_LENGTH = 12;

/**
 * First sign-in with the one-time details the owner shared. The account has
 * no portal access until its holder picks a password only they know.
 */
export default function SetPassword() {
  const { user, displayName, completePasswordSetup, signOut } = useAdminAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await completePasswordSetup(password);
    } catch (err: any) {
      setError(err?.message || 'Could not save your password. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070b13] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-transparent.png" alt="H-Plate" className="w-16 h-16 mx-auto mb-4 object-contain logo-glow" />
          <h1 className="font-display text-2xl text-white tracking-tight">
            Welcome{displayName ? `, ${displayName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-gray-400 mt-1">{user?.email}</p>
        </div>
        <Card className="border-white/10 shadow-2xl" style={{ background: 'rgba(17,24,39,0.7)' }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-cyan-300" />
              Choose your password
            </CardTitle>
            <CardDescription className="text-gray-400">
              You signed in with temporary details. Pick a password only you know. Your access to the portal starts once it is saved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-300 text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-gray-300">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`At least ${MIN_LENGTH} characters`}
                  className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-gray-300">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full text-white font-semibold border-0"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)' }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save and continue
              </Button>
              <button type="button" onClick={() => void signOut()} className="w-full text-xs text-gray-500 hover:text-gray-300">
                Sign out
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
